import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../Context/auth";
import Layout from "../../Component/Layout/Layout";
import {
  FaShare,
  FaCopy,
  FaChevronDown,
  FaChevronUp,
  FaUser,
  FaUserCheck,
  FaUserTimes,
  FaUsers,
  FaLink,
  FaChartLine,
} from "react-icons/fa";
import { FiUser, FiUserX, FiUserCheck } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import { toast } from "react-toastify";
import { useTheme } from "../../Context/ThemeContext";
import { motion } from "framer-motion";

const Teams = () => {
  const [referralTree, setReferralTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auth] = useAuth();
  const { darkMode } = useTheme();
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    inactiveReferrals: 0,
  });
  const [showReferralLink, setShowReferralLink] = useState(false);
  const referralLink = `${window.location.origin}/register?ref=${auth.user?.referralCode}`;

  useEffect(() => {
    const fetchReferralTree = async () => {
      try {
        const response = await axios.get(
          "https://bioopay-full-backend.onrender.com/api/v1/users/referral-tree",
          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
        );

        setReferralTree(response.data.tree);
        const { data: userData } = await axios.get(
          "https://bioopay-full-backend.onrender.com/api/v1/users/user/profile"
        );
        setUserData(userData.user);

        setStats({
          totalReferrals: response.data.totalReferrals,
          activeReferrals: response.data.activeReferrals,
          inactiveReferrals: response.data.inactiveReferrals,
        });
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch referral tree"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReferralTree();
  }, [auth.token]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(userData.referralLink);
    toast.success("Referral link copied to clipboard!", {
      theme: darkMode ? "dark" : "light",
    });
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "Join me on this platform!",
          text: `Sign up using my referral link to get special benefits. My code: ${userData.referralLink}`,
          url: userData.referralLink,
        })
        .catch((err) =>
          toast.error("Error sharing: " + err.toString(), {
            theme: darkMode ? "dark" : "light",
          })
        );
    } else {
      setShowReferralLink(!showReferralLink);
      copyToClipboard();
    }
  };

  if (loading)
    return (
      <Layout>
        <div className="loader-container">
          <motion.div
            className="loader-spinner"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, loop: Infinity, ease: "linear" }}
          >
            <HiOutlineSparkles size={40} />
          </motion.div>
          <p>Building your network visualization...</p>
        </div>
      </Layout>
    );

  if (error)
    return (
      <Layout title={"BIooPay- Teams"}>
        <div className="error-container">
          <motion.div
            className="error-icon"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.6 }}
          >
            !
          </motion.div>
          <p>{error}</p>
          <motion.button
            onClick={() => window.location.reload()}
            className="retry-button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Try Again
          </motion.button>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <style jsx>{`
        .teams-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: "Inter", sans-serif;
          color: ${darkMode ? "#e2e8f0" : "#2d3748"};
          min-height: 100vh;
        }

        .teams-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .teams-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 10px;
          background: ${darkMode
            ? "linear-gradient(to right, #8b5cf6, #ec4899)"
            : "linear-gradient(to right, #4f46e5, #10b981)"};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -0.5px;
        }

        .teams-subtitle {
          font-size: 1.1rem;
          color: ${darkMode ? "#a0aec0" : "#718096"};
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .stats-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: ${darkMode ? "#2d3748" : "#ffffff"};
          border-radius: 16px;
          padding: 25px;
          box-shadow: ${darkMode
            ? "0 4px 6px rgba(0, 0, 0, 0.2)"
            : "0 4px 6px rgba(0, 0, 0, 0.05)"};
          transition: all 0.3s ease;
          text-align: center;
          border-left: 5px solid;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(to right, #4f46e5, #8b5cf6);
          opacity: 0.7;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: ${darkMode
            ? "0 10px 25px rgba(0, 0, 0, 0.3)"
            : "0 10px 25px rgba(0, 0, 0, 0.1)"};
        }

        .stat-card.total {
          border-color: #4f46e5;
        }

        .stat-card.active {
          border-color: #10b981;
        }

        .stat-card.inactive {
          border-color: #ef4444;
        }

        .stat-icon {
          font-size: 1.8rem;
          margin-bottom: 15px;
          color: ${darkMode ? "#a0aec0" : "#718096"};
        }

        .stat-card h3 {
          margin: 0;
          font-size: 1rem;
          color: ${darkMode ? "#a0aec0" : "#718096"};
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-card p {
          margin: 10px 0 0;
          font-size: 2.2rem;
          font-weight: 800;
        }

        .stat-card.total p {
          color: #818cf8;
        }

        .stat-card.active p {
          color: #34d399;
        }

        .stat-card.inactive p {
          color: #f87171;
        }

        .referral-section {
          background: ${darkMode ? "#2d3748" : "#ffffff"};
          border-radius: 16px;
          padding: 30px;
          box-shadow: ${darkMode
            ? "0 4px 6px rgba(0, 0, 0, 0.2)"
            : "0 4px 6px rgba(0, 0, 0, 0.05)"};
          margin-bottom: 40px;
          border: 1px solid ${darkMode ? "#4a5568" : "#e2e8f0"};
        }

        .referral-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${darkMode ? "#f7fafc" : "#2d3748"};
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .share-controls {
          display: flex;
          gap: 12px;
        }

        .share-button {
          background: ${darkMode ? "#4f46e5" : "#4f46e5"};
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
          box-shadow: ${darkMode
            ? "0 2px 10px rgba(79, 70, 229, 0.4)"
            : "0 2px 10px rgba(79, 70, 229, 0.2)"};
        }

        .share-button:hover {
          background: ${darkMode ? "#4338ca" : "#4338ca"};
          transform: translateY(-2px);
        }

        .copy-button {
          background: ${darkMode ? "#4a5568" : "#e0e7ff"};
          color: ${darkMode ? "#ffffff" : "#4f46e5"};
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .copy-button:hover {
          background: ${darkMode ? "#4f46e5" : "#c7d2fe"};
          color: white;
          transform: translateY(-2px);
        }

        .referral-link-container {
          margin-top: 20px;
          background: ${darkMode ? "#1a202c" : "#f8fafc"};
          border-radius: 12px;
          padding: 18px;
          display: ${showReferralLink ? "flex" : "none"};
          align-items: center;
          justify-content: space-between;
          word-break: break-all;
          border: 1px solid ${darkMode ? "#4a5568" : "#e2e8f0"};
          transition: all 0.3s ease;
        }

        .referral-link {
          font-family: "Fira Code", monospace;
          color: ${darkMode ? "#818cf8" : "#4f46e5"};
          font-size: 0.9rem;
          flex-grow: 1;
        }

        .tree-container {
          background: ${darkMode ? "#2d3748" : "#ffffff"};
          border-radius: 16px;
          padding: 30px;
          box-shadow: ${darkMode
            ? "0 4px 6px rgba(0, 0, 0, 0.2)"
            : "0 4px 6px rgba(0, 0, 0, 0.05)"};
          position: relative;
          overflow: hidden;
          border: 1px solid ${darkMode ? "#4a5568" : "#e2e8f0"};
        }

        .tree-header {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${darkMode ? "#f7fafc" : "#2d3748"};
          margin-bottom: 25px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        /* New Tree Structure Styles */
        .tree-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
          overflow-x: auto;
          padding: 20px 0;
        }

        .tree {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          min-width: fit-content;
        }

        .user-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          margin: 0 10px;
          z-index: 2;
        }

        .main-user {
          margin-bottom: 40px;
        }

        .main-user .user-circle {
          width: 120px;
          height: 120px;
          background: ${darkMode
            ? "linear-gradient(135deg, #7c3aed 0%, #c026d3 100%)"
            : "linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%)"};
          color: white;
          box-shadow: ${darkMode
            ? "0 10px 25px rgba(124, 58, 237, 0.4)"
            : "0 10px 25px rgba(79, 70, 229, 0.3)"};
          border: none;
        }

        .user-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          background: ${darkMode ? "#2d3748" : "#ffffff"};
          box-shadow: ${darkMode
            ? "0 4px 15px rgba(0, 0, 0, 0.3)"
            : "0 4px 15px rgba(0, 0, 0, 0.1)"};
          border: 3px solid;
        }

        .user-circle::before {
          content: "";
          position: absolute;
          inset: -5px;
          border-radius: 50%;
          padding: 2px;
          background: ${darkMode
            ? "linear-gradient(135deg, #7c3aed, #c026d3)"
            : "linear-gradient(135deg, #4f46e5, #8b5cf6)"};
          -webkit-mask: linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .user-circle:hover::before {
          opacity: 1;
        }

        .user-circle:hover {
          transform: scale(1.08);
          box-shadow: ${darkMode
            ? "0 8px 30px rgba(0, 0, 0, 0.4)"
            : "0 8px 30px rgba(0, 0, 0, 0.15)"};
        }

        .user-circle.active {
          border-color: #10b981;
        }

        .user-circle.inactive {
          border-color: #ef4444;
        }

        .user-info {
          text-align: center;
          padding: 10px;
          width: 100%;
        }

        .user-info h3,
        .user-info h4 {
          margin: 0;
          font-size: 0.85rem;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100px;
          color: ${darkMode ? "#f7fafc" : "#2d3748"};
        }

        .main-user .user-info h3 {
          font-size: 1rem;
          max-width: 110px;
        }

        .user-info p {
          margin: 5px 0 0;
          font-size: 0.75rem;
          color: ${darkMode ? "#a0aec0" : "#718096"};
          font-weight: 500;
        }

        .user-info small {
          font-size: 0.7rem;
          color: ${darkMode ? "#718096" : "#a0aec0"};
          display: block;
          margin-top: 5px;
        }

        .tree-connector {
          width: 2px;
          height: 30px;
          background: ${darkMode ? "#4a5568" : "#e2e8f0"};
          position: relative;
          z-index: 1;
          margin: 0 auto;
        }

        .children-container {
          display: flex;
          justify-content: center;
          position: relative;
          margin-top: 30px;
        }

        .children-line {
          position: absolute;
          top: -30px;
          height: 30px;
          width: 2px;
          background: ${darkMode ? "#4a5568" : "#e2e8f0"};
          left: 50%;
          transform: translateX(-50%);
        }

        .children-row {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 20px;
          position: relative;
          padding-top: 40px;
        }

        .child-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .child-connector {
          position: absolute;
          top: -20px;
          height: 20px;
          width: 2px;
          background: ${darkMode ? "#4a5568" : "#e2e8f0"};
        }

        .horizontal-connector {
          position: absolute;
          top: -20px;
          height: 2px;
          background: ${darkMode ? "#4a5568" : "#e2e8f0"};
          z-index: 1;
        }

        .toggle-children {
          position: absolute;
          bottom: -15px;
          background: ${darkMode ? "#4f46e5" : "#4f46e5"};
          border: 2px solid ${darkMode ? "#2d3748" : "#ffffff"};
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 3;
          color: white;
          font-size: 0.7rem;
          transition: all 0.2s ease;
        }

        .toggle-children:hover {
          transform: scale(1.1);
          background: ${darkMode ? "#6366f1" : "#4338ca"};
        }

        .no-referrals {
          text-align: center;
          padding: 50px 30px;
          background: ${darkMode ? "#1a202c" : "#f8fafc"};
          border-radius: 16px;
          margin-top: 30px;
          border: 1px dashed ${darkMode ? "#4a5568" : "#cbd5e0"};
        }

        .no-referrals-icon {
          font-size: 3.5rem;
          color: ${darkMode ? "#4a5568" : "#cbd5e0"};
          margin-bottom: 20px;
        }

        .no-referrals p {
          color: ${darkMode ? "#a0aec0" : "#718096"};
          margin-bottom: 25px;
          font-size: 1.1rem;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .loader-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
        }

        .loader-spinner {
          color: ${darkMode ? "#8b5cf6" : "#4f46e5"};
          margin-bottom: 20px;
        }

        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          text-align: center;
        }

        .error-icon {
          width: 60px;
          height: 60px;
          background: ${darkMode ? "#7c3aed" : "#fee2e2"};
          color: ${darkMode ? "#ffffff" : "#ef4444"};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8rem;
          font-weight: bold;
          margin-bottom: 20px;
        }

        .retry-button {
          background: ${darkMode ? "#7c3aed" : "#4f46e5"};
          color: white;
          border: none;
          padding: 12px 25px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 20px;
          transition: all 0.2s;
          box-shadow: ${darkMode
            ? "0 2px 10px rgba(124, 58, 237, 0.4)"
            : "0 2px 10px rgba(79, 70, 229, 0.2)"};
        }

        .retry-button:hover {
          background: ${darkMode ? "#6d28d9" : "#4338ca"};
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .stats-container {
            grid-template-columns: 1fr;
          }

          .teams-title {
            font-size: 2rem;
          }

          .teams-subtitle {
            font-size: 1rem;
          }

          .share-controls {
            flex-direction: column;
            width: 100%;
          }

          .share-button,
          .copy-button {
            width: 100%;
            justify-content: center;
          }

          .main-user .user-circle {
            width: 100px;
            height: 100px;
          }

          .user-circle {
            width: 70px;
            height: 70px;
          }

          .user-info h3,
          .user-info h4 {
            font-size: 0.8rem;
          }

          .user-info p {
            font-size: 0.7rem;
          }

          .children-row {
            gap: 15px;
          }
        }

        @media (max-width: 480px) {
          .teams-title {
            font-size: 1.8rem;
          }

          .section-title,
          .tree-header {
            font-size: 1.3rem;
          }

          .stat-card {
            padding: 20px;
          }

          .stat-card p {
            font-size: 2rem;
          }

          .main-user .user-circle {
            width: 90px;
            height: 90px;
          }

          .user-circle {
            width: 60px;
            height: 60px;
          }

          .children-row {
            gap: 10px;
            padding-top: 30px;
          }
        }
      `}</style>

      <div className="teams-container">
        <div className="teams-header">
          <motion.h1
            className="teams-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Your Network Empire
          </motion.h1>
          <motion.p
            className="teams-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Build your legacy by expanding your network. Each referral brings
            you closer to your goals.
          </motion.p>
        </div>

        <motion.div
          className="stats-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="stat-card total">
            <div className="stat-icon">
              <FaUsers />
            </div>
            <h3>Total Team</h3>
            <p>{stats.totalReferrals}</p>
          </div>
          <div className="stat-card active">
            <div className="stat-icon">
              <FiUserCheck />
            </div>
            <h3>Active Members</h3>
            <p>{stats.activeReferrals}</p>
          </div>
          <div className="stat-card inactive">
            <div className="stat-icon">
              <FiUserX />
            </div>
            <h3>Inactive Members</h3>
            <p>{stats.inactiveReferrals}</p>
          </div>
        </motion.div>

        <motion.div
          className="referral-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="referral-header">
            <h2 className="section-title">
              <FaLink /> Your Golden Link
            </h2>
            <div className="share-controls">
              <motion.button
                onClick={shareReferral}
                className="share-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaShare /> Share
              </motion.button>
              <motion.button
                onClick={copyToClipboard}
                className="copy-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaCopy /> Copy
              </motion.button>
            </div>
          </div>
          <motion.div
            className="referral-link-container"
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: showReferralLink ? 1 : 0,
              height: showReferralLink ? "auto" : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <code className="referral-link">{referralLink}</code>
          </motion.div>
        </motion.div>

        <motion.div
          className="tree-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <h2 className="tree-header">
            <FaChartLine />
            Team Network
          </h2>

          {referralTree && referralTree.length > 0 ? (
            <div className="tree-wrapper">
              <div className="tree">
                {/* Main User (Top of the tree) */}
                <motion.div
                  className="user-node main-user"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="user-circle">
                    <div className="user-info">
                      <h3>{auth.user?.name || "You"}</h3>
                      <p>{auth.user?.plan || "Founder"}</p>
                      <small>Leader</small>
                    </div>
                  </div>
                </motion.div>

                {/* Tree connections */}
                <div className="tree-connector"></div>

                {/* Level 1 Referrals */}
                <div className="children-container">
                  <div className="children-line"></div>
                  <div className="children-row">
                    {referralTree.map((user, index) => (
                      <ReferralNode
                        key={user._id}
                        user={user}
                        level={1}
                        index={index}
                        totalSiblings={referralTree.length}
                        position={index}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <motion.div
              className="no-referrals"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <div className="no-referrals-icon">
                <FiUser />
              </div>
              <p>
                Your network is empty right now. Share your golden link to start
                building your empire and unlock amazing rewards!
              </p>
              <motion.button
                onClick={shareReferral}
                className="share-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaShare /> Invite Members
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

// Recursive component for rendering referral nodes
const ReferralNode = ({ user, level, index, totalSiblings, position }) => {
  const [expanded, setExpanded] = useState(level < 3);
  const { darkMode } = useTheme();

  return (
    <motion.div
      className="child-node"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.5 }}
    >
      <div className="child-connector"></div>
      <div
        className="horizontal-connector"
        style={{
          width:
            totalSiblings > 1
              ? position === 0 || position === totalSiblings - 1
                ? "50%"
                : "100%"
              : "0",
          left: totalSiblings > 1 ? (position === 0 ? "50%" : "0") : "50%",
          right:
            totalSiblings > 1 && position === totalSiblings - 1
              ? "50%"
              : "auto",
        }}
      ></div>

      <div
        className={`user-circle ${user.isActive ? "active" : "inactive"}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="user-info">
          <h4>{user.userName}</h4>
          <p>{user.planName}</p>
          <small>Level {level}</small>
        </div>
      </div>

      {user.children && user.children.length > 0 && (
        <motion.div
          className="toggle-children"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </motion.div>
      )}

      {user.children && user.children.length > 0 && expanded && (
        <div className="children-container">
          <div className="children-line"></div>
          <div className="children-row">
            {user.children.map((child, idx) => (
              <ReferralNode
                key={child._id}
                user={child}
                level={level + 1}
                index={idx}
                totalSiblings={user.children.length}
                position={idx}
              />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Teams;
